import React, { useMemo, useState } from "react";
import Tree from "../../../Components/Tree/Tree";
import {
  getGroupsTree,
  updateChildrenWithParentId,
} from "../../../Components/Tree/TreeUtils";
import "./LocationsTree.scss";
import ClassNames from "classnames";
import { getIconClassName } from "../../../Findings/FindingsUtils";
import { SeemPopover, FormFieldError } from "@SeemplicityDev/shared";
import type { TreeNode } from "../../../Components/Tree/types";
import type { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";

interface LocationsTreeError {
  message?: string;
  type?: string;
}

// Consumers forward react-hook-form field errors (plain `FieldError` or the
// nested `Merge<FieldError, FieldErrorsImpl<...>>` produced for object fields).
// At runtime only `.message`/`.type` are read, and only when the error is an
// object, so accepting the RHF shapes here is truthful to the existing usage.
type LocationsTreeErrorProp =
  | LocationsTreeError
  | FieldError
  | Merge<FieldError, FieldErrorsImpl<Record<string, unknown>>>
  | boolean
  | null;

type LocationsTreeSize =
  | "xxs"
  | "xs"
  | "s"
  | "m"
  | "l"
  | "xl"
  | "xxl"
  | "max"
  | "custom";

interface LocationsTreeProps {
  value?: TreeNode | null;
  onChange: (node: TreeNode) => void;
  label?: string | null;
  root?: TreeNode[] | null;
  wrapperClassName?: string;
  size?: LocationsTreeSize;
  labelTop?: boolean;
  required?: boolean;
  hideLabelHelper?: boolean;
  error?: LocationsTreeErrorProp;
  noMargin?: boolean;
  popoverClassName?: string;
  // Forwarded by consumers (FilterSaveAs, CreateGroup) for parity with the
  // wrapped form field; not read by LocationsTree itself.
  name?: string;
  isLoading?: boolean;
}

const LocationsTree: React.FC<LocationsTreeProps> = ({
  value,
  onChange,
  label,
  root,
  wrapperClassName,
  size,
  labelTop,
  required,
  hideLabelHelper,
  error,
  noMargin,
  popoverClassName,
}) => {
  const [isOpen, toggleIsOpen] = useState(false);

  const onSelectScope = (node: TreeNode) => {
    toggleIsOpen(false);
    onChange(node);
  };

  const popoverBody = useMemo(() => {
    let groupsTree = root;
    if (!!root) {
      const newTree = updateChildrenWithParentId(root, null);
      groupsTree = getGroupsTree(newTree);
    }
    return (
      <Tree
        data={groupsTree}
        nodeOperations={{
          onEdit: () => {},
          onSelect: onSelectScope,
          onWarning: () => false,
        }}
        isSelectableNode={() => {
          return true;
        }}
        data-testid={"resources-tree"}
        selectedNode={value?.id}
        className={`dropdown-tree`}
        borderlessNodes
      />
    );
  }, [root, value]);

  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-location-tree",
    wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "has-size-xxs": size === "xxs",
      "has-size-xs": size === "xs",
      "has-size-s": size === "s",
      "has-size-m": size === "m",
      "has-size-l": size === "l",
      "has-size-xl": size === "xl",
      "has-size-xxl": size === "xxl",
      "has-size-max": size === "max",
      "has-size-custom": size === "custom",
      "has-no-margin": noMargin,
      "has-icon": value?.type,
      "hide-label-helper": hideLabelHelper,
    }
  );

  return (
    <div
      className={`${classNameWrapper} ${isOpen ? "is-focused" : ""} ${
        !value?.name ? "is-empty" : ""
      }`}
      data-testid={`scope-location-management`}
    >
      {!!label && (
        <label className={`input-label`}>
          {label}
          {!required && !hideLabelHelper && (
            <span className={"label-helper"}>Optional</span>
          )}
        </label>
      )}
      <SeemPopover
        onOuterAction={() => toggleIsOpen(false)}
        isOpen={isOpen}
        triggerClassName={"scope-menu-popover-trigger-wrap"}
        placement={"bottom-end"}
        body={popoverBody}
        useSameWidth
        triggerFullWidth
        className={`group-location-select ${
          popoverClassName && popoverClassName
        }`}
      >
        <div
          data-testid={"scope-location-management-control-button"}
          className={`input-holder ${value?.name ? "" : "placeholder-text"}`}
          onClick={() => toggleIsOpen(!isOpen)}
        >
          <div className="input-holder-inner">
            {value?.type && (
              <div className="input-holder-icon">
                <i className={`seem-icon ${getIconClassName(value?.type)}`} />
              </div>
            )}
            <div className="input-holder-text">
              {value?.name ? value?.name : "Select Parent"}
            </div>
            <div className="dropdown-indicator">
              <i className="seem-icon seem-icon-chevron-down" />
            </div>
          </div>
        </div>
      </SeemPopover>
      {!!error && typeof error === "object" && (
        // A child of the wrapper, like every other input's error: the shared
        // error is positioned against the wrapper's reserved bottom padding.
        <FormFieldError
          // RHF field errors widen `.message`/`.type` to include nested
          // FieldError shapes, but at runtime these are plain strings; narrow
          // to the string the FormFieldError props expect.
          errorMessage={error.message as string | undefined}
          errorType={error.type as string | undefined}
          isOpen={!isOpen}
        />
      )}
    </div>
  );
};

export default LocationsTree;
